import React from 'react';
import { Button } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import FeedMatchPhotoAdd from './FeedMatchPhotoAdd';
import MatchRequestButton from './MatchRequestButton';
import { MATCH_NOTE_WINDOW_MS } from '../../shared/utils/league-match-note';
import type { MatchRequestKind } from '../../shared/utils/match-request';

const DAY_MS = 24 * 60 * 60 * 1000;

type Props = {
  event: LeagueFeed.Event;
  viewer?: Users.User;
  // eslint-disable-next-line no-unused-vars -- callback type signature
  onPhotoChange: (eventId: string, hasPhoto: boolean) => void;
  onAddNote: () => void;
};

/**
 * Maçın oyuncusuna tek satır "Maçını anlat" (3 Kasım, kaybedenin kartı sadeleştirme + C4): fotoğraf
 * ekle, not ekle ve eksik olanları rakipten tek düğmeyle iste. Önceden bu eylemler kartın üç ayrı
 * yerine dağılmıştı (en çok 8 eylem); istek düğmeleri soluk bağlantıydı ve hiç kullanılmamıştı.
 * Süreler sunucudakiyle aynı (onaydan sonra 4 gün); son karar sunucuda.
 */
export default function FeedMatchStory({ event, viewer, onPhotoChange, onAddNote }: Props) {
  const { t } = useTranslation('leagues');
  const { veri } = event;
  if (!viewer?.id || !veri.mac_id) return null;
  const opponent = [veri.kazanan, veri.kaybeden].find(player => player && player.id !== viewer.id);
  if (!opponent || ![veri.kazanan?.id, veri.kaybeden?.id].includes(viewer.id)) return null;
  const remaining = event.olusturma + MATCH_NOTE_WINDOW_MS - Date.now();
  if (remaining <= 0) return null;
  const notes = veri.notlar || [];
  const ownNote = notes.some(note => note.oyuncu_id === viewer.id);
  const missing: MatchRequestKind[] = [
    ...(veri.mac_foto_var_mi ? [] : (['foto'] as const)),
    ...(notes.some(note => note.oyuncu_id === opponent.id) ? [] : (['not'] as const)),
  ];
  if (veri.mac_foto_var_mi && ownNote && !missing.length) return null;
  const days = Math.max(1, Math.ceil(remaining / DAY_MS));
  return (
    <div className="feed-match-story" role="group" aria-label={t('detail.feed.storyActions')}>
      <span className="feed-match-story__label">{t('detail.feed.storyActions')}</span>
      <FeedMatchPhotoAdd event={event} viewer={viewer} onChange={onPhotoChange} />
      {!ownNote && (
        <Button
          className="feed-match-story__note"
          icon={<EditOutlined aria-hidden="true" />}
          onClick={onAddNote}
        >
          {t('detail.feed.addNote', { count: days })}
        </Button>
      )}
      {missing.length > 0 && (
        <MatchRequestButton key={missing.join('-')} matchId={veri.mac_id} kinds={missing} />
      )}
    </div>
  );
}

FeedMatchStory.defaultProps = { viewer: undefined };
